/**
 * Image Handler
 *
 * Orchestrates image selection, validation, preview generation, and state updates.
 */

import { ImageCanvas } from "./image-canvas";
import { ImageRender } from "./image-render";
import { ImageValidate } from "./image-validate";
import { imageForm } from "./image-form";

export interface ExistingImageData {
    id: number;
    url: string;
    filename: string;
}

export interface ImageHandlerConfig {
    maxFiles: number;
    maxSize: string | number;
    acceptedTypes: string | string[];
    existingImages: ExistingImageData[];
}

interface ImageItem {
    file: File;
    src?: string;
    url?: string;
    rotation?: number;
    originalFileName?: string;
    isNew?: boolean;
    id?: number;
}

interface ImageFormDataItem {
    name: string | undefined;
    data: File;
    rotation: number;
}

class ImageHandler {
    public postKey: string;
    public mode: string;
    public config: ImageHandlerConfig;
    public shadowImages: ImageItem[];
    public newImages: ImageItem[];
    public removedExistingImages: string[];
    public container!: HTMLElement;
    public fileInput!: HTMLInputElement;
    public addBtn!: HTMLButtonElement;
    public imageGrid!: HTMLElement;
    public dropHint!: HTMLElement;

    public constructor(postKey: string, mode: string, options: Partial<ImageHandlerConfig> = {}) {
        this.postKey = postKey;
        this.mode = mode;

        const defaults: ImageHandlerConfig = {
            maxFiles: 10,
            maxSize: "10MB",
            acceptedTypes: "image/*",
            existingImages: [],
        };

        this.config = { ...defaults, ...options };
        this.shadowImages = [];
        this.newImages = [];
        this.removedExistingImages = [];
    }

    public updateFormInput(): void {
        if (this.mode === "edit") {
            const removedInput = imageForm.getRemovedAttachedImagesInput(this.container);
            if (removedInput) {
                removedInput.value = JSON.stringify(this.removedExistingImages);
            }
        }
    }

    public getRemovedExistingImageFilenames(): string[] {
        if (this.mode !== "edit") return [];
        return Array.from(new Set(this.removedExistingImages)).filter(Boolean);
    }

    public static handleFileSelection(handler: ImageHandler, files: File[]): void {
        for (const file of files) {
            const currentCount =
                handler.mode === "create"
                    ? handler.shadowImages.length
                    : (handler.config.existingImages?.length || 0) +
                      handler.newImages.length -
                      handler.removedExistingImages.length;

            if (!ImageValidate.validateFile(file, handler.config, currentCount)) continue;

            if (handler.mode === "create") {
                ImageHandler.addImageToShadowImages(handler, file);
            } else {
                ImageHandler.addImageToNewImages(handler, file);
            }
        }
    }

    public static addImageToShadowImages(handler: ImageHandler, file: File): void {
        const placeholder = ImageRender.createLoadingPlaceholder();
        handler.imageGrid.appendChild(placeholder);

        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
            const result = typeof event.target?.result === "string" ? event.target.result : "";
            // Convert to in-memory File immediately while the data URL (and source URI) is valid.
            // On Android, content:// URIs from the file picker can expire before form submission.
            const inMemoryFile = result ? ImageHandler.dataURLtoFile(result, file.name) : file;
            const imageItem: ImageItem = {
                file: inMemoryFile,
                src: result,
                rotation: 0,
                originalFileName: file.name,
            };

            handler.shadowImages.push(imageItem);
            placeholder.remove();
            ImageRender.renderImageGrid(handler.imageGrid, handler.shadowImages, 0);
            handler.updateFormInput();
        };

        reader.onerror = () => {
            placeholder.remove();
        };

        reader.readAsDataURL(file);
    }

    public static addImageToNewImages(handler: ImageHandler, file: File): void {
        const placeholder = ImageRender.createLoadingPlaceholder();
        handler.imageGrid.appendChild(placeholder);

        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
            const result = typeof event.target?.result === "string" ? event.target.result : "";
            const inMemoryFile = result ? ImageHandler.dataURLtoFile(result, file.name) : file;
            const imageData: ImageItem = {
                file: inMemoryFile,
                src: result,
                rotation: 0,
                isNew: true,
                originalFileName: file.name,
            };

            handler.newImages.push(imageData);
            const imagesToRender = handler.newImages;

            placeholder.remove();
            ImageRender.renderImageGrid(handler.imageGrid, imagesToRender, 0);
            handler.updateFormInput();
        };

        reader.onerror = () => {
            placeholder.remove();
        };

        reader.readAsDataURL(file);
    }

    public static dataURLtoFile(dataurl: string, filename: string): File {
        const arr = dataurl.split(",");
        const mimeMatch = arr[0]?.match(/:(.*?);/);
        const mime = mimeMatch?.[1] || "application/octet-stream";
        const bstr = atob(arr[1] || "");
        const n = bstr.length;
        const u8arr = new Uint8Array(n);
        let index = n;
        while (index--) {
            u8arr[index] = bstr.charCodeAt(index);
        }
        return new File([u8arr], filename, { type: mime });
    }

    public static handleExistingImageRotation(handler: ImageHandler, event: Event): void {
        const tile = imageForm.getExistingImageTile(imageForm.getEventTarget(event));
        if (!tile) return;

        const imgElement = imageForm.getImage(tile);
        const filename = tile.dataset.imageFilename;

        if (!filename || !imgElement) return;

        const spinner = ImageRender.createRotationSpinner();
        tile.appendChild(spinner);
        tile.classList.add("is-loading");

        const originalImg = new Image();
        originalImg.crossOrigin = "anonymous";

        originalImg.onload = () => {
            const rotatedDataURL = ImageCanvas.rotateImage(originalImg, 90);
            const rotatedFile = ImageHandler.dataURLtoFile(rotatedDataURL, filename);

            const newImageItem: ImageItem = {
                file: rotatedFile,
                originalFileName: filename,
                src: rotatedDataURL,
                rotation: 90,
                isNew: true,
            };
            handler.newImages.push(newImageItem);

            if (!handler.removedExistingImages.includes(filename)) {
                handler.removedExistingImages.push(filename);
            }

            tile.remove();
            ImageRender.renderImageGrid(handler.imageGrid, handler.newImages, 0);
            handler.updateFormInput();
        };

        originalImg.onerror = () => {
            spinner.remove();
            tile.classList.remove("is-loading");
        };

        originalImg.src = imgElement.src;
    }

    public static handleExistingImageRemoval(handler: ImageHandler, event: Event): void {
        const tile = imageForm.getExistingImageTile(imageForm.getEventTarget(event));
        if (!tile) return;

        const filename = tile.dataset.imageFilename;
        if (filename && !handler.removedExistingImages.includes(filename)) {
            handler.removedExistingImages.push(filename);
        }

        const removedIndicator = imageForm.getRemovedIndicator(tile);
        if (removedIndicator) {
            tile.style.opacity = "0.5";
            removedIndicator.style.display = "flex";
            const controls = imageForm.getMediaControls(tile);
            if (controls) controls.style.display = "none";
        } else {
            tile.remove();
        }

        handler.updateFormInput();
    }

    public static handleNewImageRotation(handler: ImageHandler, event: Event): void {
        const tile = imageForm.getNewImageTile(imageForm.getEventTarget(event));
        if (!tile) return;

        const imageIndex = parseInt(tile.dataset.imageIndex || "", 10);
        const imgElement = imageForm.getImage(tile);

        if (!imgElement || Number.isNaN(imageIndex) || !handler.newImages[imageIndex]) return;

        const imageItem = handler.newImages[imageIndex];
        ImageHandler.handleRotateImageItem(handler, imageItem, imgElement);
        handler.updateFormInput();
    }

    public static handleNewImageRemoval(handler: ImageHandler, event: Event): void {
        const tile = imageForm.getNewImageTile(imageForm.getEventTarget(event));
        if (!tile) return;

        const imageIndex = parseInt(tile.dataset.imageIndex || "", 10);
        if (Number.isNaN(imageIndex)) return;

        if (handler.newImages[imageIndex]) {
            handler.newImages.splice(imageIndex, 1);
        }

        ImageRender.renderImageGrid(handler.imageGrid, handler.newImages, 0);
        handler.updateFormInput();
    }

    public static handleCreateModeImageRotation(handler: ImageHandler, event: Event): void {
        const tile = imageForm.getMediaTile(imageForm.getEventTarget(event));
        if (!tile) return;

        const index = Array.from(handler.imageGrid.children).indexOf(tile);
        if (index < 0 || index >= handler.shadowImages.length) return;

        const imageItem = handler.shadowImages[index];
        const imgElement = imageForm.getImage(tile);

        if (!(imgElement instanceof HTMLImageElement)) return;

        ImageHandler.handleRotateImageItem(handler, imageItem, imgElement);
        handler.updateFormInput();
    }

    public static handleCreateModeImageRemoval(handler: ImageHandler, event: Event): void {
        const tile = imageForm.getMediaTile(imageForm.getEventTarget(event));
        if (!tile) return;

        const index = Array.from(handler.imageGrid.children).indexOf(tile);
        if (index < 0 || index >= handler.shadowImages.length) return;

        handler.shadowImages.splice(index, 1);

        ImageRender.renderImageGrid(handler.imageGrid, handler.shadowImages, 0);
        handler.updateFormInput();
    }

    public static handleRotateImageItem(handler: ImageHandler, item: ImageItem, imgElement: HTMLImageElement): void {
        void handler;
        const tile = imageForm.getMediaTile(imgElement);
        if (!tile) return;

        const spinner = ImageRender.createRotationSpinner();
        tile.appendChild(spinner);
        tile.classList.add("is-loading");

        const originalImg = new Image();
        originalImg.onload = () => {
            item.rotation = (item.rotation || 0) + 90;
            if (item.rotation >= 360) item.rotation = 0;

            const rotatedDataURL = ImageCanvas.rotateImage(originalImg, item.rotation);

            if (item.file) {
                item.file = ImageHandler.dataURLtoFile(rotatedDataURL, item.originalFileName || "rotated.jpg");
            }

            imgElement.src = rotatedDataURL;

            spinner.remove();
            tile.classList.remove("is-loading");
        };

        originalImg.onerror = () => {
            spinner.remove();
            tile.classList.remove("is-loading");
        };

        originalImg.src = item.src || item.url || imgElement.src;
    }

    public getFormData(): ImageFormDataItem[] {
        if (this.mode === "create") {
            return this.shadowImages.map((item) => ({
                name: item.originalFileName,
                data: item.file,
                rotation: item.rotation || 0,
            }));
        }

        if (this.mode === "edit") {
            return this.newImages.map((item) => ({
                name: item.originalFileName,
                data: item.file,
                rotation: item.rotation || 0,
            }));
        }

        return [];
    }
}

export { ImageHandler };
